export const SETUP_STEPS = [
  `arbitrage`,
  `equipment`,
  `finance-product`,
  `adders`,
  `warranty`,
  `system-pricing`,
  `incentives`,
  `vpp-programs`,
  `site`,
  `utility`,
  `greenbutton`,
] as const

export type BatterySetupStep = (typeof SETUP_STEPS)[number]

export const SETUP_STEP_LABELS: Record<BatterySetupStep, string> = {
  arbitrage: `Battery Savings`,
  equipment: `Equipment`,
  [`finance-product`]: `Loan Product`,
  adders: `Adders`,
  warranty: `Protection Plans`,
  [`system-pricing`]: `System Pricing`,
  incentives: `Incentives`,
  [`vpp-programs`]: `VPP Programs`,
  site: `Site Information`,
  utility: `Utility`,
  greenbutton: `Greenbutton Data`,
}

export type BatterySetupGroupId =
  | `contract`
  | `home-and-utility-info`
  | `pricing`
  | `product-offering`
  | `share`

type BatterySetupStepPart = {
  readonly kind: `step`
  readonly id: BatterySetupStep
  readonly label: string
  /** Part stays visible but disabled until current Battery System Pricing exists. */
  readonly requiresPricing?: true
}

/** Links to a routed drawer outside the Setup step list. */
type BatterySetupRoutePart = {
  readonly kind: `route`
  readonly id: `contract-status` | `share`
  readonly label: string
  readonly pathSuffix: BatterySetupRouteSuffix
}

export type BatterySetupRouteSuffix = `contract` | `share`

/** Runs immediately from the rail instead of opening a drawer or a step. */
type BatterySetupActionPart = {
  readonly kind: `action`
  readonly id: BatterySetupActionId
  readonly label: string
}

export type BatterySetupActionId = `export-pdf`

export type BatterySetupPart =
  | BatterySetupActionPart
  | BatterySetupRoutePart
  | BatterySetupStepPart

export type BatterySetupGroup = {
  readonly id: BatterySetupGroupId
  readonly label: string
  readonly parts: readonly [BatterySetupPart, ...BatterySetupPart[]]
}

const HOME_AND_UTILITY: BatterySetupGroup = {
  id: `home-and-utility-info`,
  label: `Home & Utility Info`,
  parts: [
    // Read-only project facts read first; Utility is the editable step.
    { kind: `step`, id: `site`, label: `Site Information` },
    { kind: `step`, id: `utility`, label: `Utility` },
    // The homeowner's own utility data. It reuses a connection an earlier
    // Solar Proposal for this home already made, so opening it is a review
    // step, not a second homeowner login.
    { kind: `step`, id: `greenbutton`, label: `Greenbutton Data` },
    // Reserve and rate-change assumptions read off the same home and utility
    // facts the two steps above save, and the calculation they feed is a
    // utility-bill comparison, not part of System Pricing.
    { kind: `step`, id: `arbitrage`, label: `Battery Savings` },
  ],
}

const PRODUCT_OFFERING: BatterySetupGroup = {
  id: `product-offering`,
  label: `Product Offering`,
  parts: [
    { kind: `step`, id: `equipment`, label: `Finance Partner & Equipment` },
    // Always listed: every financing state (no partner, Cash, missing or
    // unavailable Loan Product) is discoverable from the rail.
    { kind: `step`, id: `finance-product`, label: `Loan Product` },
  ],
}

const PRICING: BatterySetupGroup = {
  id: `pricing`,
  label: `Pricing`,
  parts: [
    { kind: `step`, id: `adders`, label: `Adders` },
    // The Protection drawer opens before the first pricing calculation: a
    // selection is a pricing input, never a pricing result.
    { kind: `step`, id: `warranty`, label: `Protection Plans` },
    { kind: `step`, id: `system-pricing`, label: `System Pricing` },
    {
      kind: `step`,
      id: `incentives`,
      label: `Incentives`,
      requiresPricing: true,
    },
    // No requiresPricing: a VPP estimate is calculated from battery equipment
    // and the Finance Plan, so it does not wait on a pricing calculation.
    { kind: `step`, id: `vpp-programs`, label: `VPP Programs` },
  ],
}

const CONTRACT: BatterySetupGroup = {
  id: `contract`,
  label: `Contract`,
  parts: [
    {
      kind: `route`,
      id: `contract-status`,
      label: `Status`,
      pathSuffix: `contract`,
    },
  ],
}

const SHARE: BatterySetupGroup = {
  id: `share`,
  label: `Share`,
  parts: [
    {
      kind: `route`,
      id: `share`,
      label: `Share`,
      pathSuffix: `share`,
    },
    // Generates the brochure in place: no drawer, and no published link
    // needed, so it sits beside Share rather than inside it.
    {
      kind: `action`,
      id: `export-pdf`,
      label: `Export To PDF`,
    },
  ],
}

export const SETUP_GROUPS: readonly BatterySetupGroup[] = [
  HOME_AND_UTILITY,
  PRODUCT_OFFERING,
  PRICING,
  CONTRACT,
  SHARE,
]

const GROUP_BY_STEP: Record<BatterySetupStep, BatterySetupGroup> = {
  site: HOME_AND_UTILITY,
  utility: HOME_AND_UTILITY,
  greenbutton: HOME_AND_UTILITY,
  arbitrage: HOME_AND_UTILITY,
  equipment: PRODUCT_OFFERING,
  [`finance-product`]: PRODUCT_OFFERING,
  adders: PRICING,
  warranty: PRICING,
  [`system-pricing`]: PRICING,
  incentives: PRICING,
  [`vpp-programs`]: PRICING,
}

const GROUP_BY_ID: Record<BatterySetupGroupId, BatterySetupGroup> = {
  [`home-and-utility-info`]: HOME_AND_UTILITY,
  [`pricing`]: PRICING,
  [`product-offering`]: PRODUCT_OFFERING,
  contract: CONTRACT,
  share: SHARE,
}

export const groupForStep = (
  step: BatterySetupStep | null,
): BatterySetupGroup | null => (step == null ? null : GROUP_BY_STEP[step])

export const isBatterySetupStep = (
  value: string | undefined,
): value is BatterySetupStep => SETUP_STEPS.some((step) => step === value)

/**
 * Steps whose drawer takes nearly the whole viewport at every width. Greenbutton
 * Data is a review surface with charts, so it cannot share the screen with the
 * Setup rail the way the narrow right-anchored step drawers do.
 */
export const stepFillsViewport = (step: BatterySetupStep | null): boolean =>
  step === `greenbutton`

export const isBatterySetupGroupId = (
  value: string | undefined,
): value is BatterySetupGroupId =>
  value != null && Object.hasOwn(GROUP_BY_ID, value)

export const batteryContractPath = (proposalId: string): string =>
  `/battery-proposal/${proposalId}/contract`

export const batterySharePath = (proposalId: string): string =>
  `/battery-proposal/${proposalId}/share`

/** Route Chromium loads to render a brochure PDF. Mirrors the gateway's
 * BROCHURE_PRINT_PATH; the token is the whole authorization. */
export const BATTERY_PRINT_PRESENTATION_PATH = `/print/battery-presentation`

export const batteryFinancingStatusPath = (proposalId: string): string =>
  `/battery-proposal/${proposalId}/financing-status`

export const batteryProposalPath = (proposalId: string): string =>
  `/battery-proposal/${proposalId}`

export const batterySetupMenuPath = (proposalId: string): string =>
  `${batteryProposalPath(proposalId)}/setup`

export const batterySetupGroupPath = (
  proposalId: string,
  groupId: BatterySetupGroupId,
): string => `${batterySetupMenuPath(proposalId)}/${groupId}`

export const batterySetupStepPath = (
  proposalId: string,
  step: BatterySetupStep,
): string => `${batteryProposalPath(proposalId)}/${step}`

const pathSegments = (pathname: string): string[] =>
  pathname.split(`/`).filter(Boolean)

const lastPathSegment = (pathname: string): string | undefined =>
  pathSegments(pathname).at(-1)

export const stepFromPath = (pathname: string): BatterySetupStep | null => {
  const segment = lastPathSegment(pathname)
  return isBatterySetupStep(segment) ? segment : null
}

export const isBatteryContractPath = (pathname: string): boolean => {
  const segments = pathSegments(pathname)
  return (
    segments[0] === `battery-proposal` &&
    segments.length === 3 &&
    segments[2] === `contract`
  )
}

export const isBatteryFinancingStatusPath = (pathname: string): boolean => {
  const segments = pathSegments(pathname)
  return (
    segments[0] === `battery-proposal` &&
    segments.length === 3 &&
    segments[2] === `financing-status`
  )
}

export const isBatterySharePath = (pathname: string): boolean => {
  const segments = pathSegments(pathname)
  return (
    segments[0] === `battery-proposal` &&
    segments.length === 3 &&
    segments[2] === `share`
  )
}

export const groupFromPath = (pathname: string): BatterySetupGroup | null => {
  const segment = lastPathSegment(pathname)
  if (isBatterySetupStep(segment)) {
    return GROUP_BY_STEP[segment]
  }
  if (segment === `contract` && isBatteryContractPath(pathname)) {
    return CONTRACT
  }
  if (segment === `share` && isBatterySharePath(pathname)) {
    return SHARE
  }
  if (isBatterySetupGroupId(segment)) {
    return GROUP_BY_ID[segment]
  }
  return null
}

export const isBatterySetupMenuOpen = (pathname: string): boolean => {
  const segments = pathSegments(pathname)
  if (segments[0] !== `battery-proposal` || segments.length < 3) {
    return false
  }

  const tail = segments.slice(2)
  if (tail[0] === `setup`) {
    return (
      tail.length === 1 || (tail.length === 2 && isBatterySetupGroupId(tail[1]))
    )
  }

  if (tail.length === 1 && isBatterySetupStep(tail[0])) {
    return true
  }

  // Contract Status and Share keep the Setup rail open so Close can return to
  // the group.
  return tail.length === 1 && (tail[0] === `contract` || tail[0] === `share`)
}
